// Chamados e protocolo (Fase 6).
//
// Três coisas passam por RPC em vez de INSERT direto, e não é preferência:
//
//  • Abrir chamado gera o protocolo, cria o ticket e vincula a conversa. Em
//    três chamadas separadas, um erro no meio deixaria protocolo consumido sem
//    ticket, ou ticket sem conversa nenhuma.
//  • O protocolo é gerado no banco. Dois atendentes clicando no mesmo segundo
//    pelo app produziriam o mesmo número — testado com 20 aberturas em
//    paralelo, 20 protocolos distintos e sem buraco.
//  • Escalar para a TI grava a solicitação na Central E amarra o ticket a ela.
//    Meio caminho aqui vira solicitação órfã na fila da TI.
//
// Leitura, sim, é query normal: a RLS já limita o que cada um enxerga.

import { supabase } from '../../../src/lib/supabase';
import type { Chamado, TicketPrioridade, TicketStatus } from './tipos';

/** Abre o chamado a partir da conversa aberta. Devolve o ticket com protocolo. */
export async function abrirChamado(args: {
  conversationId: string;
  assunto: string;
  prioridade?: TicketPrioridade;
  descricao?: string | null;
}): Promise<Chamado> {
  const { data, error } = await supabase.rpc('inbox_abrir_chamado', {
    p_conversation_id: args.conversationId,
    p_assunto: args.assunto,
    p_prioridade: args.prioridade ?? 'normal',
    p_descricao: args.descricao ?? null,
  });
  if (error) throw new Error(error.message);
  // A função devolve a linha; o PostgREST entrega objeto ou array de um.
  return (Array.isArray(data) ? data[0] : data) as Chamado;
}

export async function carregarChamado(ticketId: string): Promise<Chamado | null> {
  const { data, error } = await supabase
    .from('inbox_tickets_lista')
    .select('*')
    .eq('id', ticketId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Chamado) ?? null;
}

export interface FiltroChamados {
  orgId: string;
  status?: TicketStatus | null;
  busca?: string;
  limite?: number;
}

export async function carregarChamados(f: FiltroChamados): Promise<Chamado[]> {
  let q = supabase
    .from('inbox_tickets_lista')
    .select('*')
    .eq('org_id', f.orgId)
    .order('created_at', { ascending: false })
    .limit(f.limite ?? 100);

  if (f.status) q = q.eq('status', f.status);

  const termo = (f.busca ?? '').trim();
  if (termo) {
    const escapado = termo.replace(/[%,()]/g, ' ');
    q = q.or(`protocolo.ilike.%${escapado}%,assunto.ilike.%${escapado}%,contato_nome.ilike.%${escapado}%`);
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Chamado[];
}

/** Conversas que respondem pelo mesmo protocolo. */
export async function conversasDoChamado(ticketId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('inbox_ticket_conversations')
    .select('conversation_id')
    .eq('ticket_id', ticketId);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: { conversation_id: string }) => r.conversation_id);
}

/** É isto que faz o chamado atravessar canais. */
export async function vincularConversa(ticketId: string, conversationId: string): Promise<void> {
  const { error } = await supabase.rpc('inbox_vincular_conversa_ao_chamado', {
    p_ticket_id: ticketId,
    p_conversation_id: conversationId,
  });
  if (error) throw new Error(error.message);
}

export async function mudarStatusChamado(ticketId: string, status: TicketStatus): Promise<void> {
  const patch: Record<string, unknown> = { status };
  // Resolver carimba a hora; reabrir limpa o carimbo, senão o relatório de
  // tempo de resolução ficaria contando a partir da primeira vez que alguém
  // achou que estava resolvido.
  if (status === 'resolvido' || status === 'fechado') patch.resolvido_em = new Date().toISOString();
  else patch.resolvido_em = null;

  const { error } = await supabase.from('inbox_tickets').update(patch).eq('id', ticketId);
  if (error) throw new Error(error.message);
}

export async function atribuirChamado(ticketId: string, userId: string | null): Promise<void> {
  const { error } = await supabase.from('inbox_tickets').update({ assignee_id: userId }).eq('id', ticketId);
  if (error) throw new Error(error.message);
}

/**
 * Escalar para a TI: cria a solicitação na Central e amarra as duas pontas.
 *
 * O banco recusa a segunda tentativa — sem isso, dois cliques viram duas
 * solicitações idênticas, e a duplicata só aparece do outro lado da fila.
 */
export async function escalarParaTi(
  ticketId: string,
  detalhe?: string | null,
  tipo: 'bug' | 'melhoria' | 'correcao_dados' | 'integracao' = 'bug',
): Promise<number> {
  const { data, error } = await supabase.rpc('inbox_escalar_chamado_ti', {
    p_ticket_id: ticketId,
    p_detalhe: detalhe ?? null,
    p_tipo: tipo,
  });
  if (error) throw new Error(error.message);
  return data as number;
}
